import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from 'react-helmet-async';
import Splash from "./pages/Splash";
import Match from "./pages/Match";
import Profile from "./pages/Profile";
import Auth from "./pages/Auth";
import Chat from "./pages/Chat";
import Settings from "./pages/Settings";
import NotFound from "./pages/NotFound";
import Onboarding from "./pages/Onboarding";
import Showcase from "./pages/Showcase";
import SeasonalChallenges from "./pages/SeasonalChallenges";
import LearningCircles from "./pages/LearningCircles";
import CircleDetail from "./pages/CircleDetail";
import CommunityForums from "./pages/CommunityForums";
import AIAssistant from "./pages/AIAssistant";
import VideoSessions from "./pages/VideoSessions";
import Explore from "./pages/Explore";
import Install from "./pages/Install";
import Notifications from "./pages/Notifications";
import { useAuth } from "./hooks/useAuth";
import { useDeepLinks } from "./hooks/useDeepLinks";
import InstallPrompt from "./components/InstallPrompt";
import { VideoCallProvider } from "./components/VideoCallProvider";

const queryClient = new QueryClient();

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  return <>{children}</>;
};

// Deep link handler component
const DeepLinkHandler = ({ children }: { children: React.ReactNode }) => {
  useDeepLinks();
  return <>{children}</>;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <DeepLinkHandler>
            <VideoCallProvider>
              <InstallPrompt />
              <Routes>
                <Route path="/" element={<Splash />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
                {/* Semi-public routes - viewable by anyone, actions require auth */}
                <Route path="/swipe" element={<Match />} />
                <Route path="/showcase" element={<Showcase />} />
                <Route path="/challenges" element={<SeasonalChallenges />} />
                <Route path="/circles" element={<LearningCircles />} />
                <Route path="/circles/:id" element={<CircleDetail />} />
                <Route path="/forums" element={<CommunityForums />} />
                <Route path="/explore" element={<Explore />} />
                {/* Protected routes - require authentication */}
                <Route path="/chat" element={<ProtectedRoute><Chat /></ProtectedRoute>} />
                <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
                <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                <Route path="/ai-assistant" element={<ProtectedRoute><AIAssistant /></ProtectedRoute>} />
                <Route path="/video-sessions" element={<ProtectedRoute><VideoSessions /></ProtectedRoute>} />
                <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
                <Route path="/install" element={<Install />} />
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
            </VideoCallProvider>
          </DeepLinkHandler>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
